<template>
	<div id="wrapper">
		<template v-if="!isLoading">
			<router-view />
		</template>
		<Loader v-if="isLoading" />
		<transition name="fade">
			<expired-cart-modal v-if="isCartRecreated" dark-theme />
		</transition>
		<portal to="unavailable-event-modal">
			<transition name="fade">
				<ticketing-info-modal
					v-if="isTicketingUnavailable || isEventTicketUnavailable"
					:title="ticketingUnavailableTitle"
					:additional-text="ticketingUnavailableText"
					@close="closeTicketingUnavailableModal"
				/>
			</transition>
		</portal>
	</div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Loader from '../components/Loaders/Loader.vue'
import ExpiredCartModal from '@/components/modals/ExpiredCartModal.vue'
import TicketingInfoModal from '@/components/modals/Ticketing/TicketingInfoModal.vue'

import {
	PAYMENT_GATEWAY,
	WEBSOCKET_EVENT_TYPES,
	WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE,
	WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT,
	WEB_SOCKET_STATE,
	WebsocketStringMessage
} from '@/constants'
import { buildTicketingFakeCreditCard } from '@/constantsTicketingFakePayment'
import {
	ENABLE_TICKETING_PAYMENT_TYPE_QUERY_BRIDGE,
	TICKETING_PAYMENT_TYPE_QUERY_KEY,
	normalizeTicketingPaymentType
} from '@/ticketingPaymentTypeQueryBridge'

let isPingPongRunning = false

import routerMixin from '@/mixins/routerMixin'

export default {
	name: 'Main',

	mixins: [routerMixin],

	components: { TicketingInfoModal, ExpiredCartModal, Loader },

	data() {
		return {
			isLoading: false,
			isTicketingUnavailable: false,
			isEventTicketUnavailable: false,
			pingTimeout: 0,
			reconnectTimeout: 0
		}
	},

	watch: {
		isTabStatusCreated(val) {
			if (val && this.$route.name === 'processing') {
				this.goToCheck()
			}
		},

		async isWaitingPos(val) {
			if (val && this.$route.name !== 'processing' && !this.isTabFailed) {
				await this.goToProcessing()
			}
		},

		isTabNotEditable(val) {
			if (val && this.$route.name !== 'check') {
				this.goToCheck()
			}
		},

		userId() {
			if (this.userId) {
				this.listenWebsockets()
			}
		}
	},

	async created() {
		const {
			subDomain,
			venueChainId,
			qrCodeId,
			eventTicketId
		} = this.$route.params
		const routeName = this.$route.name
		const forbiddenRoutes = ['cart', 'check', 'processing']
		const isTicketingUnavailable = this.$route.query.isTicketingUnavailable
		const isPaymentMethodPage = routeName === 'payment-method'
		this.isLoading = true

		const isVenueInfoUnavailable = await this.checkVenueInfoUnavailability(
			subDomain,
			venueChainId
		)

		if (isVenueInfoUnavailable) return

		try {
			const isOrderingUnavailable = await this.checkOrderUnavailability(
				forbiddenRoutes,
				routeName,
				isTicketingUnavailable
			)

			if (isOrderingUnavailable) return

			await this.loginUser()

			const isPaymentPageLoadedSuccessfully = await this.paymentPageLoad(
				isPaymentMethodPage,
				eventTicketId
			)

			if (isPaymentPageLoadedSuccessfully) return

			await Promise.all([this.getUser(), this.getCreditCards(), this.getCart()])
			await this.checkProductRedirection(routeName)
			await this.checkQrCode(qrCodeId)
			this.isLoading = false
			this.checkIsTicketingUnavailable(isTicketingUnavailable)
		} catch (e) {
			this.isLoading = false
		}
	},

	mounted() {
		if (this.caPaymentProvider === this.$options.PP_MAGENSA) {
			this.mountTEconnect()
		}
	},

	onBeforeDestroy() {
		if (this.pingTimeout) clearTimeout(this.pingTimeout)

		if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout)
	},

	computed: {
		...mapState('user', {
			token: 'token',
			userId: 'id'
		}),
		...mapState('venue', ['venueInfo', 'qrInfo', 'caPaymentProvider']),
		...mapGetters('cart', [
			'isTabOpened',
			'isTabStatusCreated',
			'isTabNotEditable',
			'isWaitingPos',
			'openedTab'
		]),
		...mapGetters('payment', [
			'magensaPaymentInstance',
			'tecPrInterface',
			'defaultCreditCard'
		]),
		...mapState('cart', {
			cartId: 'id',
			isCartRecreated: 'isCartRecreated'
		}),
		...mapState('cart', [
			'cartItems',
			'status',
			'tableId',
			'isTabFailed',
			'creditCardId'
		]),
		...mapState('events', ['eventTicket']),

		isCartHasProducts() {
			return this.cartItems && !!this.cartItems.length
		},

		ticketingUnavailableTitle() {
			return this.isTicketingUnavailable
				? 'Event Tickets Unavailable'
				: 'This link is not valid anymore'
		},

		ticketingUnavailableText() {
			return this.isTicketingUnavailable
				? 'Tickets are no longer available for purchase at this time'
				: ''
		}
	},

	methods: {
		...mapActions('venue', [
			'getVenueInfo',
			'getQrInfo',
			'setAllOrderingAvailable'
		]),
		...mapActions('user', ['login', 'getUser']),
		...mapActions('cart', [
			'updateCart',
			'getCart',
			'setIsTabFailed',
			'getCartByEventTicket',
			'openTab'
		]),
		...mapActions('cart', {
			resetCart: 'resetState'
		}),
		...mapActions('payment', [
			'getCreditCards',
			'mountTEconnect',
			'addCreditCard'
		]),
		...mapActions('ui', ['addMainError']),
		...mapActions('events', ['getEventTicket']),

		async checkProductRedirection(routeName) {
			if (!this.isCartHasProducts && routeName === 'cart') {
				await this.goToProducts()
			}

			if (!this.isTabOpened && routeName === 'check') {
				await this.goToProducts()
			}
		},

		async checkVenueInfoUnavailability(subDomain, venueChainId) {
			try {
				await this.getVenueInfo({
					venueChainSubdomain: subDomain,
					venueId: venueChainId
				})

				this.setAllOrderingAvailable()
			} catch (e) {
				await this.$router.push({ name: 'notFound' })
				this.isLoading = false

				return true
			}
		},

		async paymentPageLoad(isPaymentMethodPage, eventTicketId) {
			if (isPaymentMethodPage) {
				try {
					await this.callPaymentMethodPageRequests(eventTicketId)

					if (this.isTabOpened) {
						await this.goToCheck()

						return false
					}

					const selectedPaymentType = this.resolvePaymentTypeFromQuery()

					if (selectedPaymentType) {
						await this.setDefaultCreditCardOnPaymentPage(selectedPaymentType)
					} else if (!this.creditCardId) {
						await this.setDefaultCreditCardOnPaymentPage(
							PAYMENT_GATEWAY.CREDIT_CARD
						)
					} else {
						await this.updateCustomerCartData()
					}

					await this.openTab({
						cartId: this.cartId
					})

					if (this.isWaitingPos) {
						await this.goToProcessing()
					} else {
						await this.goToCheck()
					}

					this.isLoading = false

					return true
				} catch (e) {
					await this.goToProducts()
					this.isEventTicketUnavailable = true
				}
			}
		},

		resolvePaymentTypeFromQuery() {
			if (!ENABLE_TICKETING_PAYMENT_TYPE_QUERY_BRIDGE) return ''

			const queryPaymentType = this.$route?.query?.[
				TICKETING_PAYMENT_TYPE_QUERY_KEY
			]

			if (typeof queryPaymentType !== 'string') return ''

			return normalizeTicketingPaymentType(queryPaymentType)
		},

		async setDefaultCreditCardOnPaymentPage(
			paymentMethod = PAYMENT_GATEWAY.CREDIT_CARD
		) {
			const {
				email,
				firstName,
				lastName,
				phone
			} = this.eventTicket.eventPurchaseOrder

			let creditCard = this.defaultCreditCard
			const isCardPaymentType = paymentMethod === PAYMENT_GATEWAY.CREDIT_CARD

			if (!creditCard || !isCardPaymentType) {
				creditCard = await this.addCreditCard(
					buildTicketingFakeCreditCard(paymentMethod)
				)
			}

			await this.updateCart({
				creditCardId: creditCard.id,
				phoneNumber: phone,
				customerNameToPrint: `${firstName} ${lastName}`,
				email
			})
		},

		async updateCustomerCartData() {
			const {
				email,
				firstName,
				lastName,
				phone
			} = this.eventTicket.eventPurchaseOrder

			await this.updateCart({
				phoneNumber: phone,
				customerNameToPrint: `${firstName} ${lastName}`,
				email
			})
		},

		async callPaymentMethodPageRequests(eventTicketId) {
			await this.getEventTicket({
				eventTicketId,
				venueId: this.venueInfo.id
			})
			await Promise.all([
				this.getUser(),
				this.getCartByEventTicket(eventTicketId)
			])
		},

		async checkQrCode(qrCodeId) {
			if (
				qrCodeId &&
				!this.openedTab.location &&
				this.venueInfo.settings?.isLocationRequestEnabled
			) {
				const resp = await this.getQrInfo(qrCodeId)
				await this.updateCart({
					tableId: resp.tableId,
					location: resp.tableName
				})
			}
		},

		async loginUser() {
			if (!this.token) {
				await this.login()
			}
		},

		async checkOrderUnavailability(
			forbiddenRoutes,
			routeName,
			isTicketingUnavailable
		) {
			if (!this.venueInfo.acceptOnlineOrder) {
				if (forbiddenRoutes.includes(routeName)) {
					await this.goToProducts()
				}

				this.isLoading = false
				this.checkIsTicketingUnavailable(isTicketingUnavailable)

				return true
			}
		},

		checkIsTicketingUnavailable(isTicketingUnavailable) {
			if (isTicketingUnavailable) {
				this.showTicketingUnavailability()
			}
		},

		closeTicketingUnavailableModal() {
			this.isTicketingUnavailable = false
			this.isEventTicketUnavailable = false
		},

		showTicketingUnavailability() {
			this.isTicketingUnavailable = true
		},

		listenWebsockets(isReconnect = false) {
			if (
				this.$websocket &&
				(this.$websocket.readyState === WEB_SOCKET_STATE.connecting ||
					this.$websocket.readyState === WEB_SOCKET_STATE.open)
			) {
				this.$websocket.close(
					WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE,
					WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT
				)
			}

			this.$socketConnect(this.userId)

			if (isReconnect) {
				this.getCart()
			}

			this.$websocket.onclose = info => {
				isPingPongRunning = false

				if (
					info &&
					info.code === WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE &&
					info.reason === WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT
				) {
					return
				}

				this.reconnectTimeout = setTimeout(() => {
					this.listenWebsockets(true)
				}, 3000)
			}

			this.$websocket.onopen = () => {
				isPingPongRunning = true
				this.$websocket.send(WebsocketStringMessage.ping)
			}

			this.$websocket.onmessage = e => {
				if (e.data === WebsocketStringMessage.pong) {
					if (isPingPongRunning) {
						this.pingTimeout = setTimeout(() => {
							this.$websocket.send(WebsocketStringMessage.ping)
						}, 5000)
					}

					return
				}

				const data = JSON.parse(e.data)
				switch (WEBSOCKET_EVENT_TYPES[data.type]) {
					case 'TAB_CREATED':
						this.getCart()
						break
					case 'TAB_FAILED':
						this.setIsTabFailed(true)
						break
					case 'TAB_CHANGED':
						this.getCart()
						break
					case 'PAYMENT_SUCCESS':
						this.getCart()
						break
					case 'PAYMENT_FAILED':
						this.getCart()
						break
					case 'CLOSED_AUTO_CLOSEOUT':
						this.getCart()
						break
				}
			}
			this.$websocket.onerror = () => {
				console.log('-------WEBSOCKET ERROR---')
			}
		}
	}
}
</script>
